import { useMemo, forwardRef, useState, useEffect } from "react"
import { inject, observer } from "mobx-react"
import Grid from '@material-ui/core/Grid'
import MenuBookIcon from '@material-ui/icons/MenuBook';

import TypographyPro from "themes/TypographyPro"
import { useStores } from "hooks/useStores"
import { useCurrentUrlInfo } from "hooks/useCurrentUrlInfo";

import type _User from 'mobx/User'

import { getTranslatedTextByKey, routeToPage } from 'utils/utils'
import { Page, EXTERNAL_ADDRESSBOX_ID } from 'utils/constants'

import LocalizedContent from "./LocalizedContent";

import { 
    ToMenuButtonContainer, ToMenuButton, Container, StyledTextGrid, GridContainer, LocalizedLeftContainer,
    TextContainer, OrderButton, OrderButtonStripeContainer, OrderButtonStripe, LocalizedContainer 
} from './style'

const LocalizationHeader = inject(
	'User',
    'Infra',
    'Home'
)(
    observer(forwardRef(({ User }: {User: typeof _User}, ref) => {
        const { localizationStore } = useStores()
        const { pathname } = useCurrentUrlInfo()
        const [alreadyLocalized, setAlreadyLocalized] = useState(false)

        useEffect(() => {
            setAlreadyLocalized(localizationStore.alreadyLocalized)
        }, [localizationStore.alreadyLocalized])

        const translationKeys = useMemo(() => ({
                title: getTranslatedTextByKey('webFlowHomePageHeader'),
                subTitle: getTranslatedTextByKey('webFlowHomePageSubHeader'),
            }), [User.preferredLanguage])

        const textExists = translationKeys?.title || translationKeys?.subTitle
        const page = routeToPage(pathname)
        const displayToMenuButton = localizationStore.config().displayToMenuButton && page !== Page.MENU

        return (
            <Container ref={ref} id={EXTERNAL_ADDRESSBOX_ID}>
                {!alreadyLocalized &&
                    <GridContainer container justifyContent='center' alignItems="center">
                        {textExists &&
                            <StyledTextGrid item xs='auto' sm={6} $alreadyLocalized={localizationStore.alreadyLocalized}>
                                <TextContainer>
                                    {translationKeys?.title &&
                                        <TypographyPro variant="HomePageTitle" component="h1" data-testid="address-box-title">
                                            {translationKeys?.title}
                                        </TypographyPro>
                                    }
                                    {translationKeys?.subTitle &&
                                        <TypographyPro variant="BodySmall" component="h2" data-testid="address-box-subtitle">
                                            {translationKeys?.subTitle}
                                        </TypographyPro>
                                    }
                                </TextContainer>
                            </StyledTextGrid>
                        }
                        <Grid item xs sm={6}>
                            <OrderButton onClick={localizationStore.start}>
                                <TypographyPro variant="h2">
                                    {getTranslatedTextByKey('webviewFlow.startOrder', 'Order now')}
                                </TypographyPro>
                                <OrderButtonStripeContainer>
                                    <OrderButtonStripe />
                                    <OrderButtonStripe />
                                    <OrderButtonStripe />
                                </OrderButtonStripeContainer>
                            </OrderButton>
                        </Grid>
                    </GridContainer>
                }

                {alreadyLocalized && 
                    <LocalizedContainer>
                        <LocalizedLeftContainer>
                            <LocalizedContent />
                        </LocalizedLeftContainer>
                        {displayToMenuButton && 
                            <ToMenuButtonContainer>
                                <ToMenuButton onClick={localizationStore.goToLocalizedMenu}>
                                    <MenuBookIcon /> 
                                    <span>{getTranslatedTextByKey('toMenu', 'to menu')}</span>
                                </ToMenuButton>
                            </ToMenuButtonContainer>
                        }
                    </LocalizedContainer>
                }

            </Container>
        )
    }
)))

export default LocalizationHeader